import { KeyboardEventHandler, useEffect, useRef, useState } from 'react'
import { useTheme } from '@opallabs/hooks'
import { AutoExpandTextArea } from '@opallabs/lapidary'
import { FieldInputProps, TextField } from '../../types'
import { useSwappableField } from '../../useSwappableField'
import { TextDisplay } from '../TextDisplay'
import * as styles from './styles'

export const FieldText = ({
  editing,
  field,
  onChange,
  readOnly,
  setIsEditing,
  textOptions = {},
  valueLeadingNode,
}: FieldInputProps<TextField>) => {
  const theme = useTheme()
  const { lineClamp, maxLength = 4000 } = textOptions
  const [inputValue, setInputValue] = useState(field.data?.value?.value ?? '')
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const { setDisplayRef, onStopEditing } = useSwappableField({ setIsEditing })

  // Sync local state with prop changes (e.g., when data is updated externally)
  useEffect(() => {
    setInputValue(field.data?.value?.value ?? '')
  }, [field.data?.value?.value])

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      inputRef.current?.blur()
    }
    // Blurring saves the edit. A containing panel that also closed on this
    // Escape would unmount the field before it saves.
    if (e.key === 'Escape') {
      e.stopPropagation()
      inputRef.current?.blur()
    }
  }

  const onBlur = () => {
    onStopEditing()

    if (inputValue !== field.data?.value?.value) {
      return onChange({
        fieldId: field.id,
        data: {
          id: field.data?.id || null,
          value: inputValue ? { value: inputValue } : null,
        },
      })
    }
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()

      const valueLength = field.data?.value?.value?.length
      if (valueLength)
        inputRef.current?.setSelectionRange(valueLength, valueLength)
    }
  }, [editing, field.data?.value?.value])

  if (readOnly || !editing) {
    return (
      <TextDisplay
        ref={setDisplayRef}
        fieldId={field.id}
        fieldLabel={field.displayName}
        leadingNode={valueLeadingNode}
        lineClamp={lineClamp}
        readOnly={readOnly}
        setIsEditing={setIsEditing}
        text={field.data?.value?.value}
      />
    )
  }

  return (
    <>
      <AutoExpandTextArea
        ref={inputRef}
        css={styles.textareaOverrides}
        id={`field-${field.id}`}
        onBlur={onBlur}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        maxRows={lineClamp}
        showCounter={false}
        value={inputValue}
      />
      {inputValue.length >= maxLength && (
        <span css={styles.characterText(theme)}>
          This field accepts a max of {maxLength} characters.
        </span>
      )}
    </>
  )
}
